import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { createPortal } from "react-dom";

export function Button({ className = "", variant = "primary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "quiet" | "danger" }) {
  return <button className={`button button--${variant} ${className}`} {...props} />;
}

export function Icon({ name, size = 22 }: { name: "today" | "orders" | "summary" | "products" | "staff" | "search" | "plus" | "close" | "back" | "bell" | "wifi" | "chevron" | "print"; size?: number }) {
  const paths: Record<string, ReactNode> = {
    today: <><path d="M4 5.5h16v14H4z"/><path d="M8 3v5M16 3v5M4 10h16"/></>,
    orders: <><path d="M6 3h12v18H6z"/><path d="M9 8h6M9 12h6M9 16h4"/></>,
    summary: <><path d="M5 20V10M12 20V4M19 20v-7"/><path d="M3 20h18"/></>,
    products: <><path d="M4 8.5 12 4l8 4.5v9L12 22l-8-4.5z"/><path d="M12 13v9M4 8.5l8 4.5 8-4.5"/></>,
    staff: <><circle cx="9" cy="8" r="3"/><path d="M3.5 20v-2.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V20M16 6.5a3 3 0 0 1 0 5.8M17 14a4 4 0 0 1 3.5 4v2"/></>,
    search: <><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></>,
    plus: <path d="M12 5v14M5 12h14"/>,
    close: <path d="m6 6 12 12M18 6 6 18"/>,
    back: <path d="m15 18-6-6 6-6"/>,
    bell: <><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 8h18c0-1-3-1-3-8"/><path d="M10 21h4"/></>,
    wifi: <><path d="M4 9a12 12 0 0 1 16 0M7 13a7 7 0 0 1 10 0M10 17a3 3 0 0 1 4 0"/><circle cx="12" cy="20" r=".6" fill="currentColor"/></>,
    chevron: <path d="m9 18 6-6-6-6"/>,
    print: <><path d="M7 9V3h10v6M7 18H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M7 14h10v7H7z"/></>,
  };
  return <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  children?: ReactNode;
};

export function Field({ label, hint, error, children }: FieldProps) {
  return <label className={`field ${error ? "field--error" : ""}`}>
    <span className="field__label">{label}</span>
    {children}
    {hint && !error ? <span className="field__hint">{hint}</span> : null}
    {error ? <span className="field__error" role="alert">{error}</span> : null}
  </label>;
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input" {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="input textarea" {...props} />;
}

export function Spinner({ label = "読み込み中" }: { label?: string }) {
  return <div className="loading" role="status"><span className="spinner"/><span>{label}</span></div>;
}

export function Empty({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="empty"><div className="empty__loaf" aria-hidden="true"/><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Notice({ tone = "info", children }: { tone?: "info" | "success" | "warning" | "error"; children: ReactNode }) {
  return <div className={`notice notice--${tone}`} role={tone === "error" ? "alert" : "status"}>{children}</div>;
}

export function Modal({ title, children, footer, onClose, wide = false, dirty = false }: { title: string; children: ReactNode; footer?: ReactNode; onClose: () => void; wide?: boolean; dirty?: boolean }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  const close = () => {
    if (dirty && !window.confirm("入力中の内容を破棄しますか？")) return;
    onClose();
  };
  return createPortal(
    <dialog ref={dialogRef} className={`modal ${wide ? "modal--wide" : ""}`} aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); close(); }}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="modal__panel">
        <header className="modal__header"><h2 id={titleId}>{title}</h2><button className="icon-button" type="button" onClick={close} aria-label="閉じる"><Icon name="close"/></button></header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </section>
    </dialog>,
    document.body,
  );
}

export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "new" | "confirmed" | "ready" | "picked_up" | "cancelled" | "success"; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}
